import { getQueryOptions, useSuspenseQuery } from '~/services'
import { useSuspenseQuery as useTanstackSuspenseQuery } from '@tanstack/react-query'
import { COMMUNITY_PLAN_PRODUCT_ID, PLUS_PLAN_PRODUCT_ID, TEAM_PLAN_PRODUCT_ID } from '../constants'
import { getLegacyNameFromPlanId } from '../utils/plan'
import { useActiveSubscription } from './useActiveSubscription'
import { PlanProduct } from '../types/index'
import Stripe from 'stripe'
import { useGetWorkspacePreferences } from '~/hooks/users/useGetWorkspacePreferences'

export const usePlanProducts = (workspaceId: string) => {
  const { data: products } = useSuspenseQuery('workspaces_/$workspaceId_/billing/products', { workspaceId })
  const { data: subscriptionSchedule } = useSuspenseQuery('workspaces_/$workspaceId_/billing/subscriptionSchedule', {
    workspaceId,
  })
  const activeSubscriptions = useActiveSubscription(workspaceId)
  const { data: cancelledToCommunity } = useGetWorkspacePreferences({ path: 'cancelledToCommunity' }, workspaceId)

  const { plan: workspacePlan } = useTanstackSuspenseQuery({
    ...getQueryOptions('workspaces_/$workspaceId_', { workspaceId }),
    queryKey: [],
    gcTime: 0,
    staleTime: 0,
  }).data

  const currentPlan: PlanProduct =
    workspacePlan === 'enterprise'
      ? {
          id: 'enterprise',
          cycle: 'yearly',
          legacyName: 'enterprise',
          priceId: '',
          pricePerMonth: 0,
        }
      : getSubscriptionPlan(activeSubscriptions)

  // The only plan that does not have a yearly counterpart is the community plan
  const planProducts: PlanProduct[] = products
    .filter((product) => {
      return [COMMUNITY_PLAN_PRODUCT_ID, PLUS_PLAN_PRODUCT_ID, TEAM_PLAN_PRODUCT_ID].includes(product.id)
    })
    .flatMap((product) => {
      const monthlyPrice = product.prices.find((price) => price.recurring?.interval === 'month')
      const yearlyPrice = product.prices.find((price) => price.recurring?.interval === 'year')
      const legacyName = getLegacyNameFromPlanId(product.id)

      const results: PlanProduct[] = []

      if (monthlyPrice) {
        results.push({
          id: product.id,
          legacyName,
          cycle: 'monthly',
          priceId: monthlyPrice.id,
          pricePerMonth: monthlyPrice.unit_amount ?? 0,
        })

        if (legacyName === 'community') {
          results.push({
            id: product.id,
            legacyName,
            cycle: 'yearly',
            priceId: monthlyPrice.id,
            pricePerMonth: monthlyPrice.unit_amount ?? 0,
          })
        }
      }

      if (yearlyPrice) {
        results.push({
          id: product.id,
          legacyName,
          cycle: 'yearly',
          priceId: yearlyPrice.id,
          pricePerMonth: (yearlyPrice.unit_amount ?? 0) / 12,
        })
      }

      return results
    })

  const currentPhase = subscriptionSchedule.phases?.[0]
  const nextPhase = subscriptionSchedule.phases?.[1]
  const nextPhasePlan = getNextPhasePlan(currentPlan, planProducts, !!cancelledToCommunity?.value, nextPhase)

  return {
    currentPlan,
    currentPhaseEndDate: currentPhase?.end_date,
    nextPhasePlan,
    planProducts,
  }
}

const getSubscriptionPlan = (activeSubscriptions: {
  monthly: Stripe.Subscription | undefined
  yearly: Stripe.Subscription | undefined
}): PlanProduct => {
  const yearlySubscriptionPlan = activeSubscriptions.yearly?.items.data.find(
    (item) =>
      [COMMUNITY_PLAN_PRODUCT_ID, PLUS_PLAN_PRODUCT_ID, TEAM_PLAN_PRODUCT_ID].includes(item.price.product.toString()) &&
      item.quantity === 1
  )

  const monthlySubscriptionPlan = activeSubscriptions.monthly?.items.data.find(
    (item) =>
      [COMMUNITY_PLAN_PRODUCT_ID, PLUS_PLAN_PRODUCT_ID, TEAM_PLAN_PRODUCT_ID].includes(item.price.product.toString()) &&
      item.quantity === 1
  )

  if (
    yearlySubscriptionPlan &&
    (!activeSubscriptions.yearly?.cancel_at_period_end ||
      (activeSubscriptions.yearly?.cancel_at_period_end && !monthlySubscriptionPlan))
  )
    return {
      id: yearlySubscriptionPlan.price.product.toString(),
      legacyName: getLegacyNameFromPlanId(yearlySubscriptionPlan.price.product.toString()),
      cycle: 'yearly',
      priceId: yearlySubscriptionPlan.price.id,
      pricePerMonth: (yearlySubscriptionPlan.price.unit_amount ?? 0) / 12,
      cancelAtPeriodEnd: activeSubscriptions.yearly?.cancel_at_period_end ?? false,
    }

  if (monthlySubscriptionPlan)
    return {
      id: monthlySubscriptionPlan.price.product.toString(),
      legacyName: getLegacyNameFromPlanId(monthlySubscriptionPlan.price.product.toString()),
      cycle: 'monthly',
      priceId: monthlySubscriptionPlan.price.id,
      pricePerMonth: monthlySubscriptionPlan.price.unit_amount ?? 0,
    }

  return {
    id: COMMUNITY_PLAN_PRODUCT_ID,
    legacyName: 'community',
    cycle: 'monthly',
    priceId: '',
    pricePerMonth: 0,
  }
}

const getNextPhasePlan = (
  currentPlan: PlanProduct,
  planProducts: PlanProduct[],
  cancelledToCommunity: boolean,
  nextPhase?: Stripe.SubscriptionSchedule.Phase
): PlanProduct => {
  if (currentPlan.cycle === 'yearly' || currentPlan.legacyName === 'enterprise') return currentPlan

  const monthlyCommunityPlan = planProducts
    .filter((plan) => plan.cycle === 'monthly')
    .find((plan) => plan.id === COMMUNITY_PLAN_PRODUCT_ID)!

  if (cancelledToCommunity) return monthlyCommunityPlan
  if (!nextPhase?.items) return currentPlan

  const nextPhasePlan = planProducts.find((product) => {
    const nextPhaseItemsPriceIds = nextPhase.items.map((item) => {
      const priceId = typeof item.price === 'string' ? item.price : item.price.id
      return priceId
    })

    return nextPhaseItemsPriceIds.includes(product.priceId)
  })

  return nextPhasePlan ?? currentPlan
}
